import { useEffect, useState } from "react";
import { Bell, Mail, MessageCircle, Copy, Check, AlertTriangle } from "lucide-react";
import { ConfirmationModal } from "./ConfirmationModal";
import * as api from "../services/api";
import { useTranslation } from "react-i18next";
import styles from "./NotificationManager.module.css";

interface NotificationManagerProps {
  clientId: string;
  pushEnabled?: boolean;
  emailEnabled?: boolean;
  whatsappEnabled?: boolean;
  emailAvailable?: boolean;
  whatsappAvailable?: boolean;
  pushAvailable?: boolean;
  disabled?: boolean;
}

const NotificationManager: React.FC<NotificationManagerProps> = ({
  clientId,
  pushEnabled: initialPush = false,
  emailEnabled: initialEmail = false,
  whatsappEnabled: initialWhatsapp = false,
  emailAvailable = true,
  whatsappAvailable = true,
  pushAvailable = true,
  disabled = false,
}) => {
  const { t, i18n } = useTranslation();

  const [pushEnabled, setPushEnabled] = useState(initialPush);
  const [emailEnabled, setEmailEnabled] = useState(initialEmail);
  const [whatsappEnabled, setWhatsappEnabled] = useState(initialWhatsapp);
  const [isLoading, setIsLoading] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const [modalState, setModalState] = useState({
    isOpen: false,
    title: "",
    message: "",
  });

  useEffect(() => {
    setPushEnabled(initialPush);
    setEmailEnabled(initialEmail);
    setWhatsappEnabled(initialWhatsapp);
  }, [initialPush, initialEmail, initialWhatsapp]);

  const updateNotification = async (
    type: "push" | "email" | "whatsapp",
    enabled: boolean,
  ) => {
    setIsLoading(type);

    try {
      const data = await api.updateNotificationSettings(clientId, {
        pushEnabled: type === "push" ? enabled : pushEnabled,
        emailEnabled: type === "email" ? enabled : emailEnabled,
        whatsappEnabled: type === "whatsapp" ? enabled : whatsappEnabled,
      });

      setPushEnabled(data.pushEnabled);
      setEmailEnabled(data.emailEnabled);
      setWhatsappEnabled(data.whatsappEnabled);

      const typeLabels: Record<string, string> = {
        push: t("notifications.pushLabel"),
        email: t("notifications.emailLabel"),
        whatsapp: t("notifications.whatsappLabel"),
      };

      setModalState({
        isOpen: true,
        title: enabled ? t("notifications.enabled") : t("notifications.disabled"),
        message: t(
          enabled ? "notifications.enabledMessage" : "notifications.disabledMessage",
          { type: typeLabels[type] },
        ),
      });
    } catch {
      setModalState({
        isOpen: true,
        title: t("notifications.error"),
        message: t("notifications.errorMessage", { type }),
      });
    } finally {
      setIsLoading(null);
    }
  };

  const handleCopyUrl = () => {
    navigator.clipboard
      .writeText(window.location.href)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      })
      .catch(() => {});
  };

  const ua = navigator.userAgent;
  const isIOS = /iPad|iPhone|iPod/i.test(ua);
  const isStandalone =
    window.matchMedia("(display-mode: standalone)").matches ||
    (window.navigator as Navigator & { standalone?: boolean }).standalone === true;

  const isThirdPartyBrowser =
    /CriOS/i.test(ua) ||
    /FxiOS/i.test(ua) ||
    /OPiOS/i.test(ua) ||
    /EdgiOS/i.test(ua);

  const needsSafariBanner = isIOS && !isStandalone && isThirdPartyBrowser;

  if (needsSafariBanner) {
    return (
      <div className={styles.safariBanner} data-lang={i18n.language}>
        <h3 className={styles.safariTitle}>
          <Bell className="w-4 h-4" aria-hidden="true" />
          {t("notifications.safariNeededTitle")}
        </h3>
        <p className={styles.safariText}>{t("notifications.safariBannerText")}</p>
        <p className={styles.safariDesc}>{t("notifications.safariNeededDesc")}</p>
        <button type="button" onClick={handleCopyUrl} className={styles.copyBtn}>
          {copied ? (
            <>
              <Check className="w-3.5 h-3.5" aria-hidden="true" />
              {t("notifications.copied")}
            </>
          ) : (
            <>
              <Copy className="w-3.5 h-3.5" aria-hidden="true" />
              {t("notifications.copyUrl")}
            </>
          )}
        </button>
        <p className={styles.safariNote}>{t("notifications.iosRequires")}</p>
      </div>
    );
  }

  if (!("Notification" in window)) {
    return (
      <div className={styles.unsupported}>
        <AlertTriangle className="w-4 h-4 inline-block mr-1" aria-hidden="true" />
        {t("notifications.notSupported")}
      </div>
    );
  }

  const activeCount = [pushEnabled, emailEnabled, whatsappEnabled].filter(Boolean).length;

  return (
    <>
      <div className={`${styles.panel} ${disabled ? styles.disabled : ""}`}>
        <div className={styles.panelHeader}>
          <span className={styles.panelTitle}>
            <Bell className="w-3.5 h-3.5" aria-hidden="true" />
            {t("notifications.myAlerts")}
          </span>
          <span className={styles.panelCount}>
            {activeCount}/3 {t("notifications.active")}
          </span>
        </div>

        <div className={styles.toggleGrid}>
          <button
            type="button"
            onClick={() => updateNotification("push", !pushEnabled)}
            disabled={isLoading === "push" || disabled || !pushAvailable}
            className={`${styles.toggleBtn} ${pushEnabled ? styles.toggleActivePush : ""} ${!pushAvailable ? styles.toggleUnavailable : ""}`}
          >
            <Bell className={`w-4 h-4 sm:w-5 sm:h-5 ${isLoading === "push" ? "animate-pulse" : ""}`} aria-hidden="true" />
            <span>{isLoading === "push" ? "..." : t("notifications.push")}</span>
          </button>

          <button
            type="button"
            onClick={() => updateNotification("email", !emailEnabled)}
            disabled={isLoading === "email" || disabled || !emailAvailable}
            className={`${styles.toggleBtn} ${emailEnabled ? styles.toggleActiveEmail : ""} ${!emailAvailable ? styles.toggleUnavailable : ""}`}
          >
            <Mail className={`w-4 h-4 sm:w-5 sm:h-5 ${isLoading === "email" ? "animate-pulse" : ""}`} aria-hidden="true" />
            <span>{isLoading === "email" ? "..." : t("notifications.email")}</span>
          </button>

          <button
            type="button"
            onClick={() => updateNotification("whatsapp", !whatsappEnabled)}
            disabled={isLoading === "whatsapp" || disabled || !whatsappAvailable}
            className={`${styles.toggleBtn} ${whatsappEnabled ? styles.toggleActiveWhatsapp : ""} ${!whatsappAvailable ? styles.toggleUnavailable : ""}`}
          >
            <MessageCircle className={`w-4 h-4 sm:w-5 sm:h-5 ${isLoading === "whatsapp" ? "animate-pulse" : ""}`} aria-hidden="true" />
            <span>{isLoading === "whatsapp" ? "..." : t("notifications.whatsapp")}</span>
          </button>
        </div>

        <p className={styles.hint}>{t("notifications.clickToToggle")}</p>
      </div>

      <ConfirmationModal
        isOpen={modalState.isOpen}
        onClose={() => setModalState({ ...modalState, isOpen: false })}
        title={modalState.title}
        message={modalState.message}
      />
    </>
  );
};

export default NotificationManager;
